/*
 * SeoPages — 11 dedicated landing pages so every link on plugitai.com
 * resolves to real, crawlable content. Each page:
 *   • Sets its own document.title + meta description + canonical
 *   • Has a single, focused H1 (people-first + Google local-ranking guidance)
 *   • Has ≥ 3 crawlable internal links + a clear CTA
 *   • Lives on the URL Kit's SEO auditor requested
 *
 * Keeping every page in one file so we can maintain them together — they
 * share a small `SeoLayout` shell. Route wiring is in App.js.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Phone, MapPin, ArrowRight, CheckCircle2, Rocket, Palette, Bot,
  Headphones, TrendingUp, Search, Award, Users,
  Store, ShoppingCart, HardHat, RefreshCw, MapPinned, DollarSign, Trophy,
  Workflow, MessageSquare, Zap, Code2,
} from "lucide-react";
import { LIGHT_BG } from "@/lib/lightTheme";

// ── Shared utilities ────────────────────────────────────────────────────
function setSeo({ title, description, canonical }) {
  document.title = title;
  const set = (attr, key, content) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", content);
  };
  set("name", "description", description);
  set("property", "og:title", title);
  set("property", "og:description", description);
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
  link.setAttribute("href", canonical);
}

// Inject a Service + FAQPage JSON-LD block scoped to the current page. Google
// consumes @id-scoped blocks for entity-level ranking signals; we use the page
// URL as the primary identifier so each page owns its own graph.
function injectJsonLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

function useSeo(cfg) {
  useEffect(() => { setSeo(cfg); }, []);
}

// Emit a Service + FAQPage schema pair for a commercial service landing page.
// `faq` is a list of {q, a} objects, `service` is a compact Service definition.
function useServiceSchema({ url, service, faq }) {
  useEffect(() => {
    injectJsonLd(`ld-service-${service.name.replace(/\W+/g, "-").toLowerCase()}`, {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${url}#service`,
      serviceType: service.name,
      provider: {
        "@type": "Organization",
        name: "PlugitAI",
        url: "https://plugitai.com",
        telephone: "+1-214-815-5966",
        areaServed: { "@type": "AdministrativeArea", name: "Dallas–Fort Worth, TX" },
      },
      areaServed: { "@type": "AdministrativeArea", name: "Dallas–Fort Worth, TX" },
      description: service.description,
      url: url,
      ...(service.offers && { offers: service.offers }),
    });
    if (faq && faq.length) {
      injectJsonLd(`ld-faq-${service.name.replace(/\W+/g, "-").toLowerCase()}`, {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((it) => ({
          "@type": "Question",
          name: it.q,
          acceptedAnswer: { "@type": "Answer", text: it.a },
        })),
      });
    }
  }, [url, service, faq]);
}

// ── Reusable page shell ─────────────────────────────────────────────────
// Auto-adds "life" to every H1 by rendering the LAST word/phrase in an
// italic serif with a blue → emerald gradient — a signature accent inspired
// by the SEO infographic aesthetic. Works on every string H1 with no
// per-page edits. Callers can pass a JSX H1 instead to opt-out.
function accentHeadline(h1) {
  if (typeof h1 !== "string") return h1; // pass-through for JSX / custom nodes
  const trimmed = h1.replace(/\s+$/g, "");
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace < 0) {
    // Single-word H1 — accent the whole thing.
    return (
      <span
        className="italic"
        style={{
          fontFamily: "'Fraunces','Instrument Serif','Playfair Display',Georgia,serif",
          backgroundImage: "linear-gradient(135deg, #1E5FE0 0%, #22C55E 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {trimmed}
      </span>
    );
  }
  const head = trimmed.slice(0, lastSpace);
  const tail = trimmed.slice(lastSpace + 1);
  return (
    <>
      {head}{" "}
      <span
        className="italic"
        style={{
          fontFamily: "'Fraunces','Instrument Serif','Playfair Display',Georgia,serif",
          backgroundImage: "linear-gradient(135deg, #1E5FE0 0%, #22C55E 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {tail}
      </span>
    </>
  );
}

function SeoLayout({ eyebrow, h1, intro, children, testId }) {
  return (
    <main className="min-h-screen text-slate-900" style={LIGHT_BG} data-testid={testId}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-6">
        {eyebrow && (
          <div className="text-[10px] md:text-[11px] uppercase tracking-[0.32em] font-black text-blue-600 mb-4">
            {eyebrow}
          </div>
        )}
        <h1 className="font-black leading-[1.02] tracking-[-0.02em] text-slate-900"
            style={{ fontSize: "clamp(2rem, 5vw, 3.75rem)", fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}>
          {accentHeadline(h1)}
        </h1>
        {intro && <p className="mt-5 max-w-[720px] text-base md:text-lg text-slate-600 leading-relaxed">{intro}</p>}
      </div>
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pb-24">{children}</div>
    </main>
  );
}

function CTA({ to, primary, label, icon: Icon = ArrowRight, testId }) {
  return (
    <Link
      to={to}
      className={"inline-flex items-center gap-2 h-11 px-6 rounded-md font-black uppercase tracking-[0.22em] text-[11px] transition-colors " +
        (primary
          ? "bg-blue-600 text-white hover:bg-blue-700 shadow-[0_4px_16px_rgba(30,95,224,0.25)]"
          : "border border-slate-300 text-slate-900 hover:border-slate-500 hover:bg-slate-50")}
      data-testid={testId}
    >
      {label} <Icon size={14} />
    </Link>
  );
}

function CrossLinks({ items }) {
  return (
    <div className="mt-16 border-t border-slate-200 pt-8">
      <div className="text-[10px] uppercase tracking-[0.28em] font-black text-slate-400 mb-4">Related on PlugitAI</div>
      <div className="grid md:grid-cols-3 gap-3">
        {items.map((it) => (
          <Link key={it.to} to={it.to}
                className="block rounded-lg border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-blue-400 hover:shadow-[0_4px_16px_rgba(15,23,42,0.06)] transition-all"
                data-testid={`crosslink-${it.to.replace(/[^a-z0-9]/gi, "-")}`}>
            <div className="text-sm font-black text-slate-900">{it.label}</div>
            <div className="text-[11px] text-slate-500 mt-1">{it.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── 1. AI Website Design Dallas (LEGACY — cross-canonicals to /website-design-dallas
//        to consolidate duplicate Dallas-web-design intent. Both URLs stay live so
//        existing backlinks work, but Google will treat /website-design-dallas as
//        the single ranking URL.)  Aug 2026 SEO consolidation.
export function AIWebsiteDesignDallas() {
  useSeo({
    title: "AI Website Design Dallas · PlugitAI",
    description: "AI-driven website design in Dallas, TX. Full 50-page sites with built-in AI receptionist, SEO monitoring, and lead capture. Built by Kit Garcia, one of 4 certified Google Experts in Texas.",
    canonical: "https://plugitai.com/website-design-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Dallas · Fort Worth · DFW"
      h1="AI Website Design in Dallas."
      intro="Every PlugitAI site ships with an AI receptionist, SEO monitoring, and lead capture already wired in — so it works while you sleep. Built in Dallas for Dallas businesses."
      testId="page-ai-website-design-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/website-launch" primary label="See Website Launch" testId="cta-launch" />
        <CTA to="/custom-studio" label="Custom Studio" testId="cta-custom" />
        <CTA to="/#contact" label="Talk to Kit" icon={Phone} testId="cta-contact" />
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">Why Dallas businesses hire PlugitAI</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Local. Kit lives and works in DFW.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> One of 4 certified Google Experts in Texas (2017).</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Full stack: site + AI + SEO monitoring + lead capture.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Same-week turnaround on the launch tier.</li>
          </ul>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">Two doors in</h2>
          <div className="space-y-3">
            <Link to="/website-launch" className="block rounded-lg border border-blue-400/40 p-4 hover:bg-blue-50">
              <div className="text-sm font-black flex items-center gap-2"><Rocket size={14} className="text-blue-600" /> Website Launch</div>
              <div className="text-[12px] text-slate-500 mt-1">A full 50-page AI-ready site. Fastest path online.</div>
            </Link>
            <Link to="/custom-studio" className="block rounded-lg border border-slate-200 p-4 hover:bg-white/5">
              <div className="text-sm font-black flex items-center gap-2"><Palette size={14} className="text-blue-600" /> Custom Studio</div>
              <div className="text-[12px] text-slate-500 mt-1">A complete AI workforce and bespoke design. Starting at $4,000.</div>
            </Link>
          </div>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/custom-web-design-dallas", label: "Custom Web Design Dallas", desc: "Bespoke builds for Dallas brands." },
        { to: "/custom-studio/ai-workforce", label: "The AI Workforce", desc: "Six bots. Six jobs. One system." },
        { to: "/about-kit", label: "About Kit", desc: "Meet the builder behind PlugitAI." },
      ]}/>
    </SeoLayout>
  );
}

// ── 2. Custom Web Design Dallas ─────────────────────────────────────────
export function CustomWebDesignDallas() {
  useSeo({
    title: "Custom Web Design Dallas · PlugitAI",
    description: "Bespoke web design in Dallas — hand-built sites with a complete AI workforce, tailored to your brand. Starting at $4,000. Serving DFW.",
    canonical: "https://plugitai.com/custom-web-design-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Custom · Dallas / Fort Worth"
      h1="Custom Web Design, Dallas."
      intro="Not a template. Not a page builder. Every pixel, every animation, every bot — designed for your business. Starting at $4,000."
      testId="page-custom-web-design-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/custom-studio" primary label="Enter Custom Studio" testId="cta-enter-studio" />
        <CTA to="/#contact" label="Scope a project" icon={Phone} testId="cta-scope" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { t: "Bespoke design", d: "Direct art direction, not stock templates." },
          { t: "AI workforce", d: "Receptionist, sales, follow-up, SEO watch, marketing." },
          { t: "Own your stack", d: "Full CMS + database, hosted or exported." },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">{c.t}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
          </div>
        ))}
      </div>
      <CrossLinks items={[
        { to: "/custom-studio", label: "Custom Studio overview", desc: "What ships in a Custom build." },
        { to: "/custom-studio/ai-workforce", label: "AI Workforce diagram", desc: "See every bot in one map." },
        { to: "/ai-website-design-dallas", label: "AI Website Design", desc: "The faster $350 launch tier." },
      ]}/>
    </SeoLayout>
  );
}

// ── 3. Website Launch ───────────────────────────────────────────────────
export function WebsiteLaunch() {
  useSeo({
    title: "Website Launch — Same-Week Dallas Website from $299.99 · PlugitAI",
    description: "PlugitAI Website Launch — a full 50-page AI-ready site with SEO, mobile-perfect design, and an AI receptionist included. Starting at $299.99. Same-week turnaround. Dallas / Fort Worth.",
    canonical: "https://plugitai.com/website-launch",
  });
  useServiceSchema({
    url: "https://plugitai.com/website-launch",
    service: {
      name: "Website Launch Dallas · $299.99",
      description: "Full 50-page AI-ready website with SEO, mobile-perfect design, and AI receptionist included. Same-week turnaround. Dallas / Fort Worth.",
      offers: { "@type": "Offer", priceCurrency: "USD", price: "299.99", availability: "https://schema.org/InStock" },
    },
    faq: [],
  });
  return (
    <SeoLayout
      eyebrow="Launch tier · fastest path online"
      h1="Website Launch — Same-Week Dallas Website from $299.99."
      intro="A full 50-page AI-ready website. SEO, mobile-perfect design, AI receptionist included. Same-week turnaround. Built for Dallas / Fort Worth small businesses that need to be online yesterday."
      testId="page-website-launch"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=launch" primary label="Lock in $299.99" icon={Rocket} testId="cta-lock-in" />
        <CTA to="/custom-studio" label="Compare to Custom Studio" testId="cta-compare" />
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">What&#39;s included</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            {[
              "Up to 50 SEO-optimized pages",
              "Mobile-perfect responsive design",
              "AI Receptionist chat widget",
              "Google Business + schema markup",
              "Sitemap + robots.txt",
              "First-month launch support",
            ].map((f) => (
              <li key={f} className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> {f}</li>
            ))}
          </ul>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">Timeline</h2>
          <div className="text-sm text-slate-600 space-y-2">
            <div><span className="text-blue-600 font-black">Day 1</span> · Kickoff call, brand assets.</div>
            <div><span className="text-blue-600 font-black">Day 3</span> · Draft live for review.</div>
            <div><span className="text-blue-600 font-black">Day 7</span> · AI bot trained, public launch.</div>
            <div><span className="text-blue-600 font-black">Day 14</span> · SEO indexing check-in.</div>
          </div>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/ai-website-design-dallas", label: "AI Website Design Dallas", desc: "Local SEO landing page." },
        { to: "/ai-receptionist", label: "AI Receptionist details", desc: "The bot that ships with every site." },
        { to: "/case-studies/lone-star-striping", label: "Case: Lone Star Striping", desc: "How the launch tier performs live." },
      ]}/>
    </SeoLayout>
  );
}

// ── 4. Custom Studio ────────────────────────────────────────────────────
export function CustomStudio() {
  useSeo({
    title: "Custom Studio — Starting at $4,000 · PlugitAI",
    description: "PlugitAI Custom Studio — bespoke design and a complete six-bot AI workforce built around your business. Starting at $4,000.",
    canonical: "https://plugitai.com/custom-studio",
  });
  useServiceSchema({
    url: "https://plugitai.com/custom-studio",
    service: {
      name: "Custom Studio Dallas · Bespoke Web Development",
      description: "Bespoke web design + full six-bot AI workforce build. Custom code, API integrations, and complete brand experience. Dallas / Fort Worth.",
      offers: { "@type": "Offer", priceCurrency: "USD", price: "4000", availability: "https://schema.org/InStock" },
    },
    faq: [],
  });
  return (
    <SeoLayout
      eyebrow="Bespoke · complete AI workforce"
      h1="Custom Studio — Starting at $4,000."
      intro="A hand-crafted brand experience wrapped around a full six-bot AI workforce. This is what you buy when the launch tier isnisn't ambitious enough#39;t ambitious enough."
      testId="page-custom-studio"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=custom_studio" primary label="Scope a project" icon={Rocket} testId="cta-scope-custom" />
        <CTA to="/custom-studio/ai-workforce" label="See the AI Workforce" testId="cta-workforce" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { icon: Palette,    t: "Bespoke design",     d: "Direct art direction, motion, hand-cut UI." },
          { icon: Bot,        t: "Six-bot AI team",    d: "Receptionist, Lead-Qual, Sales, Follow-Up, SEO Watch, Marketing." },
          { icon: TrendingUp, t: "Scoped to your ops", d: "Systems mapped to how you actually run." },
        ].map((c) => {
          const I = c.icon;
          return (
            <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <I size={16} className="text-blue-600" />
              <div className="text-sm font-black mt-2">{c.t}</div>
              <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
            </div>
          );
        })}
      </div>
      <CrossLinks items={[
        { to: "/custom-studio/ai-workforce", label: "AI Workforce diagram", desc: "Every bot on one interactive map." },
        { to: "/custom-web-design-dallas", label: "Custom Web Design Dallas", desc: "The Dallas SEO landing." },
        { to: "/website-launch", label: "Website Launch tier", desc: "Faster path if you don't need custom." },
      ]}/>
    </SeoLayout>
  );
}

// ── 5. AI Receptionist ──────────────────────────────────────────────────
export function AIReceptionist() {
  useSeo({
    title: "AI Receptionist · PlugitAI",
    description: "PlugitAI's AI Receptionist answers website chats, captures caller details, handles common questions, and schedules appointments — 24/7.",
    canonical: "https://plugitai.com/ai-receptionist",
  });
  useServiceSchema({
    url: "https://plugitai.com/ai-receptionist",
    service: {
      name: "AI Receptionist Dallas",
      description: "24/7 AI receptionist that greets visitors, captures leads, handles common questions, and books appointments. Included with every PlugitAI website.",
    },
    faq: [],
  });
  return (
    <SeoLayout
      eyebrow="Bot 1 of 6 · Front door"
      h1="The AI Receptionist."
      intro="Answers website chats, captures caller details, handles common questions, and schedules appointments. Fewer missed opportunities, every hour of every day."
      testId="page-ai-receptionist"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=receptionist" primary label="Turn one on" icon={Headphones} testId="cta-turn-on" />
        <CTA to="/custom-studio/ai-workforce" label="See all 6 bots" testId="cta-see-all" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <article className="rounded-lg border border-slate-200 p-5 bg-white">
          <h2 className="font-black">What it does</h2>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
            <li>· Greets every visitor in your business voice.</li>
            <li>· Answers 90% of first-contact questions.</li>
            <li>· Captures name, phone, urgency + intent.</li>
            <li>· Books appointments straight into your calendar.</li>
            <li>· Hands off to a human when it matters.</li>
          </ul>
        </article>
        <article className="rounded-lg border border-slate-200 p-5 bg-white">
          <h2 className="font-black">Business result</h2>
          <div className="mt-3 text-sm text-slate-600">Fewer missed opportunities. Every visitor who lands on your site gets a real answer within seconds, not &ldquo;we&#39;ll get back to you Monday.&rdquo;</div>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/ai-sales-bot", label: "AI Sales Bot", desc: "What handles buyers after qualification." },
        { to: "/seo-monitoring", label: "SEO Monitoring", desc: "The bot that watches your rankings." },
        { to: "/custom-studio", label: "Custom Studio", desc: "Full workforce build." },
      ]}/>
    </SeoLayout>
  );
}

// ── 6. AI Sales Bot ─────────────────────────────────────────────────────
export function AISalesBot() {
  useSeo({
    title: "AI Sales Bot · PlugitAI",
    description: "PlugitAI's AI Sales Bot explains services, handles objections, recommends next steps, and hands hot prospects to a human. More conversations, more decisions.",
    canonical: "https://plugitai.com/ai-sales-bot",
  });
  useServiceSchema({
    url: "https://plugitai.com/ai-sales-bot",
    service: {
      name: "AI Sales Bot Dallas",
      description: "24/7 AI sales bot that explains services, handles objections, and hands hot leads to a human. Trained on your business.",
    },
    faq: [],
  });
  return (
    <SeoLayout
      eyebrow="Bot 3 of 6 · Closer"
      h1="The AI Sales Bot."
      intro="Explains services, handles approved objections, recommends the next step, and hands hot prospects to a person. Trained on your services, priced right, always on."
      testId="page-ai-sales-bot"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=sales_bot" primary label="Deploy on my site" icon={TrendingUp} testId="cta-deploy-sales" />
        <CTA to="/custom-studio/ai-workforce" label="See all 6 bots" testId="cta-see-all" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { t: "Explains services", d: "Your offering, in your voice, at 2 a.m." },
          { t: "Handles objections", d: "Approved rebuttals only — never off-script." },
          { t: "Hands off hot leads", d: "Ping to Kit's phone the moment intent fires." },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">{c.t}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
          </div>
        ))}
      </div>
      <CrossLinks items={[
        { to: "/ai-receptionist", label: "AI Receptionist", desc: "Bot 1 — the front door." },
        { to: "/custom-studio", label: "Custom Studio", desc: "Where the full six-bot team lives." },
        { to: "/case-studies/lone-star-striping", label: "Case Study", desc: "AI closing after hours." },
      ]}/>
    </SeoLayout>
  );
}

// ── 7. SEO Monitoring ───────────────────────────────────────────────────
export function SEOMonitoring() {
  useSeo({
    title: "SEO Monitoring · PlugitAI",
    description: "PlugitAI's SEO Watch Bot checks titles, canonicals, broken pages, sitemap health, and indexing changes — so failures are caught earlier, not months later.",
    canonical: "https://plugitai.com/seo-monitoring",
  });
  useServiceSchema({
    url: "https://plugitai.com/seo-monitoring",
    service: {
      name: "SEO Monitoring Dallas",
      description: "24/7 SEO watchdog for Dallas businesses — title drift, canonical mismatches, broken links, sitemap health, indexing loss, schema validation.",
    },
    faq: [],
  });
  return (
    <SeoLayout
      eyebrow="Bot 5 of 6 · Watchdog"
      h1="SEO Monitoring, always on."
      intro="The SEO Watch Bot checks titles, canonicals, broken pages, sitemap health, and indexing changes — then reports problems before Google notices."
      testId="page-seo-monitoring"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=seo" primary label="Get a SEO audit" icon={Search} testId="cta-audit" />
        <CTA to="/custom-studio/ai-workforce" label="See the bot map" testId="cta-map" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <article className="rounded-lg border border-slate-200 p-5 bg-white">
          <h2 className="font-black">What gets watched</h2>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-600">
            <li>· Title + meta description drift</li>
            <li>· Canonical mismatches</li>
            <li>· Broken internal + outbound links</li>
            <li>· Sitemap freshness + validation</li>
            <li>· Indexing gain/loss vs. yesterday</li>
            <li>· Schema.org validity</li>
          </ul>
        </article>
        <article className="rounded-lg border border-slate-200 p-5 bg-white">
          <h2 className="font-black">Why it matters</h2>
          <div className="mt-3 text-sm text-slate-600">Google ranks businesses on relevance, distance, and prominence. Missing meta, broken links, and dead sitemaps quietly cut all three. The Watch Bot catches them the day they happen.</div>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/ai-website-design-dallas", label: "AI Website Design Dallas", desc: "The local SEO landing." },
        { to: "/case-studies/lone-star-striping", label: "Case Study", desc: "SEO wins in the wild." },
        { to: "/custom-studio", label: "Custom Studio", desc: "The full workforce." },
      ]}/>
    </SeoLayout>
  );
}

// ── 8. Case study: Lone Star Striping ──────────────────────────────────
export function CaseLoneStar() {
  useSeo({
    title: "Case Study · Lone Star Striping Pros · PlugitAI",
    description: "How a Fort Worth striping company launched a full 50-page site, an AI SalesBot, and started closing leads at 2 a.m. — built by PlugitAI.",
    canonical: "https://plugitai.com/case-studies/lone-star-striping",
  });
  return (
    <SeoLayout
      eyebrow="Case study · Fort Worth, TX"
      h1="Lone Star Striping Pros."
      intro="Fort Worth striping company. Full 50-page site, AI SalesBot, live chat, appointment booking — all shipped in one sprint."
      testId="page-case-lone-star"
    >
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {[
          { k: "Stack", v: "Website Launch tier + SalesBot + Follow-Up + SEO Watch" },
          { k: "Location", v: "Fort Worth · Dallas · Arlington" },
          { k: "Live site", v: "lonestarstripingpros.com" },
        ].map((s) => (
          <div key={s.k} className="rounded-lg border border-slate-200 p-4 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">{s.k}</div>
            <div className="text-sm font-black mt-1">{s.v}</div>
          </div>
        ))}
      </div>
      <article className="prose prose-invert max-w-none">
        <h2 className="font-black text-xl">The setup</h2>
        <p className="text-sm text-slate-600 leading-relaxed">
          David and his team stripe parking lots across DFW. They had no site, no calendar, no way to catch after-hours calls. PlugitAI built a full 50-page site, wired the SalesBot into every page, and connected the calendar so booking happens in the chat itself.
        </p>
        <h2 className="font-black text-xl mt-6">What ships in a Launch build</h2>
        <p className="text-sm text-slate-600 leading-relaxed">
          The Launch tier ($350) includes the site + receptionist bot + SEO monitoring + schema. On Lone Star Striping we layered in the SalesBot module so it can quote basic jobs in-chat and hand hot leads to David&#39;s phone.
        </p>
      </article>
      <div className="flex flex-wrap gap-3 mt-10">
        <CTA to="/website-launch" primary label="Get the same launch tier" testId="cta-lonestar-launch" />
        <CTA to="/#contact?intent=case_lonestar" label="Talk to Kit" icon={Phone} testId="cta-lonestar-contact" />
      </div>
      <CrossLinks items={[
        { to: "/case-studies/iheartfrenchies", label: "iHeartFrenchies", desc: "Direct-to-consumer breed match." },
        { to: "/ai-sales-bot", label: "AI Sales Bot", desc: "The bot that closed after hours." },
        { to: "/ai-website-design-dallas", label: "AI Website Design Dallas", desc: "The Dallas landing page." },
      ]}/>
    </SeoLayout>
  );
}

// ── 9. Case study: iHeartFrenchies ─────────────────────────────────────
export function CaseIHeartFrenchies() {
  useSeo({
    title: "Case Study · iHeartFrenchies · PlugitAI",
    description: "A direct-to-consumer French Bulldog breeder launched with PlugitAI — bespoke design, AI receptionist, and a lead-qualification bot to filter serious buyers.",
    canonical: "https://plugitai.com/case-studies/iheartfrenchies",
  });
  return (
    <SeoLayout
      eyebrow="Case study · Texas"
      h1="iHeartFrenchies."
      intro="A French Bulldog breeder that needed to look premium and screen inquiries before they hit the phone. Bespoke design + Lead Qualification Bot + Follow-Up Bot."
      testId="page-case-iheartfrenchies"
    >
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        {[
          { k: "Stack",     v: "Custom Studio + Lead-Qual + Follow-Up" },
          { k: "Focus",     v: "Premium DTC breeder brand" },
          { k: "Highlight", v: "Auto-filters casual browsers" },
        ].map((s) => (
          <div key={s.k} className="rounded-lg border border-slate-200 p-4 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">{s.k}</div>
            <div className="text-sm font-black mt-1">{s.v}</div>
          </div>
        ))}
      </div>
      <article>
        <h2 className="font-black text-xl">The problem</h2>
        <p className="text-sm text-slate-600 leading-relaxed mt-2">
          A high-touch breeder business gets swamped with tire-kickers. We built a Custom Studio site with a Lead Qualification bot that asks the right questions (home setup, other pets, timeline, budget) before ever routing to the owner.
        </p>
        <h2 className="font-black text-xl mt-6">Business result</h2>
        <p className="text-sm text-slate-600 leading-relaxed mt-2">
          The owner spends her calls on qualified buyers, not on repeat FAQs. The Follow-Up bot re-engages ghosted leads 3 and 7 days later — automatically.
        </p>
      </article>
      <div className="flex flex-wrap gap-3 mt-10">
        <CTA to="/custom-studio" primary label="Build my Custom Studio" testId="cta-frenchies-custom" />
        <CTA to="/#contact?intent=case_frenchies" label="Talk to Kit" icon={Phone} testId="cta-frenchies-contact" />
      </div>
      <CrossLinks items={[
        { to: "/case-studies/lone-star-striping", label: "Lone Star Striping", desc: "The launch-tier case." },
        { to: "/custom-web-design-dallas", label: "Custom Web Design Dallas", desc: "The Dallas Custom landing." },
        { to: "/about-kit", label: "About Kit", desc: "The builder." },
      ]}/>
    </SeoLayout>
  );
}

// ── 10. About Kit (LEGACY alias — cross-canonicals to /about to consolidate
//        the three founder pages into a single ranking URL.  Aug 2026 SEO consolidation.)
export function AboutKitAlias() {
  useSeo({
    title: "About Kit Garcia — Founder of PlugitAI",
    description: "Chris (Kit) Garcia — founder of PlugitAI. One of 4 certified Google Experts in Texas (2017). Top-10 SEO Expert 2026. Building AI-driven websites in Dallas / Fort Worth.",
    canonical: "https://plugitai.com/about",
  });
  return (
    <SeoLayout
      eyebrow="Founder · Dallas / Fort Worth"
      h1="Kit Garcia."
      intro="Chris (Kit) Garcia — founder of PlugitAI. One of 4 certified Google Experts in Texas (2017). Top-10 SEO Expert of 2026. Two decades in auto sales taught him people first, tech second."
      testId="page-about-kit"
    >
      <div className="grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">Credentials</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><Award size={16} className="text-amber-500 shrink-0 mt-0.5" /> Google Expert · 1 of 4 in Texas (2017)</li>
            <li className="flex gap-2"><Award size={16} className="text-amber-500 shrink-0 mt-0.5" /> Top-10 SEO Expert of 2026</li>
            <li className="flex gap-2"><Award size={16} className="text-amber-500 shrink-0 mt-0.5" /> Top-20 Newest Designers</li>
            <li className="flex gap-2"><Users size={16} className="text-blue-600 shrink-0 mt-0.5" /> 20 years in auto sales · people-first roots</li>
          </ul>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">Contact</h2>
          <div className="text-sm text-slate-600 space-y-2">
            <div className="flex items-center gap-2"><Phone size={14} className="text-blue-600" /> <a href="tel:+12148155966" className="hover:text-slate-900">(214) 815-5966</a></div>
            <div className="flex items-center gap-2"><MapPin size={14} className="text-blue-600" /> Dallas / Fort Worth, TX</div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <CTA to="/#contact" primary label="Book a build" testId="cta-book-build" />
            <CTA to="/custom-studio/ai-workforce" label="See the workforce" testId="cta-see-workforce" />
          </div>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/about", label: "Full bio", desc: "The long version at /about." },
        { to: "/ai-website-design-dallas", label: "AI Website Design Dallas", desc: "What Kit builds." },
        { to: "/case-studies/lone-star-striping", label: "Case Study", desc: "Recent DFW build." },
      ]}/>
    </SeoLayout>
  );
}

// ── 11. Small Business Web Design Dallas ────────────────────────────────
export function SmallBusinessWebDesignDallas() {
  useSeo({
    title: "Small Business Web Design Dallas · PlugitAI",
    description: "Web design for Dallas small businesses under 20 employees. $299.99 launch tier, AI receptionist included, same-week turnaround. Built for owners who wear every hat.",
    canonical: "https://plugitai.com/small-business-web-design-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Owners · < 20 employees · DFW"
      h1="Small Business Web Design in Dallas."
      intro="You already stripe the lot, take the calls, write the invoices. Your website shouldn't be another job. PlugitAI ships a 50-page site with an AI receptionist wired in — for $299.99 — so leads convert while you're on the ladder."
      testId="page-small-business-web-design-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/website-launch" primary label="Lock in $299.99" icon={Store} testId="cta-smb-launch" />
        <CTA to="/#contact?intent=small_business" label="Talk to Kit" icon={Phone} testId="cta-smb-contact" />
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">The small-business build kit</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Up to 50 SEO pages (services · service-area · about · gallery · contact).</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> AI receptionist that greets, qualifies, and captures name + phone.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Google Business Profile schema so you show up on the map pack.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Mobile-perfect layout — 68% of your traffic is already on phones.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> A form that texts you the moment a lead hits submit.</li>
          </ul>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">Who this is for</h2>
          <div className="space-y-3 text-sm text-slate-600">
            <p>Roofers, HVAC techs, striping crews, landscapers, cleaners, mobile detailers, tutors, pest control, mobile mechanics — anyone in DFW under 20 employees who runs the business AND does the work.</p>
            <p>You don&#39;t need a $6,000 agency retainer. You need something that answers the phone at 8 p.m. and pushes the lead to your inbox before the competitor down the road picks up.</p>
          </div>
        </article>
      </div>
      <div className="mt-12 rounded-lg border border-slate-200 p-6 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="text-[10px] uppercase tracking-[0.28em] font-black text-blue-600 mb-2">Real example · Lone Star Striping Pros</div>
        <p className="text-sm text-slate-600 leading-relaxed">
          David and his crew stripe parking lots across Fort Worth, Dallas, and Arlington. Before PlugitAI: zero online presence, missed calls after 5 p.m., word-of-mouth only. After the $299.99 launch tier: 266+ live pages, AI SalesBot booking after-hours estimates, and a searchable presence across every DFW city where they work.
        </p>
        <div className="mt-3">
          <Link to="/case-studies/lone-star-striping" className="text-[12px] uppercase tracking-[0.24em] font-black text-blue-600 hover:text-blue-300" data-testid="cta-smb-case">
            Read the case study →
          </Link>
        </div>
      </div>
      <CrossLinks items={[
        { to: "/website-cost-dallas", label: "What does a Dallas website cost?", desc: "The honest pricing breakdown." },
        { to: "/website-launch", label: "Website Launch tier", desc: "The $299.99 build." },
        { to: "/local-seo-dallas", label: "Local SEO Dallas", desc: "Rank on the map pack." },
      ]}/>
    </SeoLayout>
  );
}

// ── 12. E-Commerce Website Design Dallas ────────────────────────────────
export function EcommerceWebsiteDesignDallas() {
  useSeo({
    title: "E-Commerce Website Design Dallas · PlugitAI",
    description: "Dallas e-commerce web design — Stripe checkout, AI product-Q&A bot, inventory sync, and SEO product pages that actually rank. Custom Studio builds start at $4,000.",
    canonical: "https://plugitai.com/ecommerce-website-design-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Product sellers · DFW"
      h1="E-Commerce Web Design, Dallas."
      intro="Selling online is a different sport. Product pages, checkout, taxes, shipping, inventory, abandoned-cart follow-ups — every piece has to work. PlugitAI builds Dallas e-commerce sites with the AI product-Q&A bot layered on top, so buyers don't bounce because they can't find sizing."
      testId="page-ecommerce-website-design-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/custom-studio" primary label="Scope an e-com build" icon={ShoppingCart} testId="cta-ecom-scope" />
        <CTA to="/#contact?intent=ecommerce" label="Talk to Kit" icon={Phone} testId="cta-ecom-contact" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { t: "Stripe checkout", d: "Real checkout, real fees. No hidden Shopify %." },
          { t: "AI product-Q&A", d: "Bot answers sizing, materials, shipping in-page." },
          { t: "Cart follow-up", d: "SMS + email drip on ghosted carts, 30 min → 3 days." },
          { t: "SEO product pages", d: "Schema.org Product + Offer + Review markup baked in." },
          { t: "Inventory sync", d: "One dashboard for stock, orders, shipping labels." },
          { t: "Google Shopping ready", d: "Merchant Center feed generated automatically." },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">{c.t}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-12 grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">Verticals we&#39;ve shipped</h2>
          <div className="text-sm text-slate-600 space-y-3">
            <p><span className="font-black text-slate-900">Telehealth GLP-1 (LeenRx, BestRx):</span> nationwide direct-to-patient sales with symptom triage bot + Stripe checkout + state-restriction logic.</p>
            <p><span className="font-black text-slate-900">Freight quoting (CargoPros):</span> instant-quote form → SMS lead capture → booking calendar. Central Valley, CA.</p>
          </div>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">What you should NOT pay for</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" /> A generic Shopify theme swap for $8k.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" /> &ldquo;SEO packages&rdquo; that are just meta-tag edits.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" /> A $299/mo maintenance retainer on a static PDF catalog.</li>
          </ul>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/custom-studio", label: "Custom Studio", desc: "Where e-com builds live." },
        { to: "/website-cost-dallas", label: "Dallas website cost", desc: "What e-com actually runs." },
        { to: "/ai-sales-bot", label: "AI Sales Bot", desc: "The bot that closes the cart." },
      ]}/>
    </SeoLayout>
  );
}

// ── 13. Contractor Website Design Dallas ────────────────────────────────
export function ContractorWebsiteDesignDallas() {
  useSeo({
    title: "Contractor Website Design Dallas · PlugitAI",
    description: "Dallas contractor web design — roofing, HVAC, striping, remodel, concrete, fencing. Instant-quote forms, before/after galleries, service-area SEO pages for every DFW city.",
    canonical: "https://plugitai.com/contractor-website-design-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Trades · roofing · HVAC · concrete · DFW"
      h1="Contractor Web Design, Dallas."
      intro="You bid the job, run the crew, and still have to sit on hold with your marketing person. PlugitAI ships contractor sites with instant-quote forms, before/after galleries, and a service-area SEO page for every DFW city you work — so the phone rings without a middleman."
      testId="page-contractor-website-design-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/website-launch" primary label="Launch my site — $299.99" icon={HardHat} testId="cta-contractor-launch" />
        <CTA to="/#contact?intent=contractor" label="Talk to Kit" icon={Phone} testId="cta-contractor-contact" />
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">The contractor stack</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> One service-area page per DFW city you cover (Dallas, Plano, Frisco, Arlington, McKinney, Denton …).</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Before / after gallery with lightbox — the #1 conversion tool for trades.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Instant-quote form → SMS to your phone within 30 seconds.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> License / insurance / bonding badges in the footer (Google trust signal).</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Emergency service section — nights, weekends, holidays.</li>
          </ul>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">Trades we&#39;ve shipped for</h2>
          <div className="text-sm text-slate-600 space-y-3">
            <p><span className="font-black text-slate-900">Striping:</span> Lone Star Striping Pros — 266+ live pages across DFW.</p>
            <p><span className="font-black text-slate-900">Freight / lumping:</span> Lone Star Lumpers — 24/7 dispatch with 60-min response guarantee.</p>
            <p><span className="font-black text-slate-900">Pest control:</span> Miss Rodriguez Pest Control — DFW residential + commercial.</p>
            <p><span className="font-black text-slate-900">Coming:</span> Roofers, HVAC, concrete, fencing, remodel — Kit is actively taking on 3 more trades this quarter.</p>
          </div>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/for/roofers", label: "For Roofers", desc: "Trade-specific landing." },
        { to: "/local-seo-dallas", label: "Local SEO Dallas", desc: "How trades win the map pack." },
        { to: "/case-studies/lone-star-striping", label: "Lone Star case study", desc: "Live contractor build." },
      ]}/>
    </SeoLayout>
  );
}

// ── 14. Website Redesign Dallas ─────────────────────────────────────────
export function WebsiteRedesignDallas() {
  useSeo({
    title: "Website Redesign Dallas · PlugitAI",
    description: "Dallas website redesign that keeps your SEO, migrates your content, adds an AI receptionist, and ships in the same week. From $299.99 for a full rebuild.",
    canonical: "https://plugitai.com/website-redesign-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Redesign · migration-safe · Dallas"
      h1="Website Redesign, Dallas."
      intro="Your current site loads in 6 seconds, breaks on iPhone, and doesn't rank for anything. But you've had that URL for 8 years and you don't want to lose the traffic you already have. PlugitAI ships a same-week redesign that carries your rankings forward, layers an AI bot on top, and starts at $299.99."
      testId="page-website-redesign-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/website-launch" primary label="Rebuild — $299.99" icon={RefreshCw} testId="cta-redesign-launch" />
        <CTA to="/#contact?intent=redesign" label="Free audit call" icon={Phone} testId="cta-redesign-audit" />
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">What a real redesign looks like</h2>
          <div className="text-sm text-slate-600 space-y-3">
            <p><span className="font-black text-slate-900">1. Crawl the old site.</span> We map every ranking URL, every backlink, every image alt tag. Nothing gets lost.</p>
            <p><span className="font-black text-slate-900">2. 301 the URLs.</span> Old page → new page, one-to-one. Your Google authority carries over.</p>
            <p><span className="font-black text-slate-900">3. Rebuild the shell.</span> Mobile-first, sub-1-second load, WCAG-compliant, schema.org markup on every page type.</p>
            <p><span className="font-black text-slate-900">4. Wire the AI receptionist.</span> Bot greets returning visitors and new ones the same way.</p>
            <p><span className="font-black text-slate-900">5. Ship in a week.</span> Draft on Day 3, live on Day 7.</p>
          </div>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">Rankings you don&#39;t lose</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> 301 redirect map (audited, not guessed).</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Canonical tags on every new page.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Google Search Console re-verification on day 1.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Sitemap resubmitted the hour we launch.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Backlink audit — we email your top 20 referrers if their link needs updating.</li>
          </ul>
        </article>
      </div>
      <CrossLinks items={[
        { to: "/local-seo-dallas", label: "Local SEO Dallas", desc: "Rank locally after the rebuild." },
        { to: "/website-cost-dallas", label: "What it costs", desc: "Honest pricing for a rebuild." },
        { to: "/seo-monitoring", label: "SEO Monitoring", desc: "Catch drops before they hurt." },
      ]}/>
    </SeoLayout>
  );
}

// ── 15. Local SEO Dallas ────────────────────────────────────────────────
export function LocalSEODallas() {
  useSeo({
    title: "Local SEO Dallas · PlugitAI",
    description: "Local SEO in Dallas — Google Business Profile optimization, service-area pages for every DFW city, citation cleanup, schema.org LocalBusiness markup, and the AI SEO Watch bot on 24/7.",
    canonical: "https://plugitai.com/local-seo-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Local search · map pack · Dallas"
      h1="Local SEO, Dallas."
      intro="Google ranks local businesses on three things: relevance, distance, and prominence. PlugitAI works all three — the site, the Google Business Profile, the citations, and the schema.org markup — then the SEO Watch bot patrols it every night so nothing quietly breaks."
      testId="page-local-seo-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=local_seo" primary label="Get a free audit" icon={MapPinned} testId="cta-local-seo-audit" />
        <CTA to="/seo-monitoring" label="See the Watch bot" testId="cta-local-seo-bot" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { t: "Google Business Profile", d: "Categories, services, photos, Q&A, weekly posts, review reply cadence." },
          { t: "Service-area SEO pages", d: "One page per DFW city you serve — schema.org LocalBusiness + FAQ on each." },
          { t: "Citation cleanup", d: "NAP consistency across Yelp, BBB, Bing Places, Apple Maps, Angi, HomeAdvisor." },
          { t: "Schema.org markup", d: "LocalBusiness, Service, FAQ, Review, Breadcrumb — everywhere it matters." },
          { t: "Review capture", d: "Post-job SMS asks for a Google review — 3× your current rate is realistic." },
          { t: "SEO Watch bot", d: "24/7 monitoring for title drift, broken links, indexing loss." },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">{c.t}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-12 rounded-lg border border-amber-300 p-6 bg-amber-50">
        <div className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-500 mb-2">Credential</div>
        <p className="text-sm text-slate-700">
          Kit Garcia (founder) — <span className="font-black">1 of 4 certified Google Experts in Texas (2017)</span> · <span className="font-black">Top-10 SEO Expert of 2026</span>. Local SEO isn&#39;t a side dish here — it&#39;s the main course.
        </p>
      </div>
      <CrossLinks items={[
        { to: "/website-design-dallas", label: "Website Design Dallas", desc: "The primary landing." },
        { to: "/contractor-website-design-dallas", label: "For contractors", desc: "Trades-specific SEO." },
        { to: "/tx", label: "DFW city index", desc: "Every service-area page live now." },
      ]}/>
    </SeoLayout>
  );
}

// ── 16. Website Cost Dallas ─────────────────────────────────────────────
export function WebsiteCostDallas() {
  useSeo({
    title: "How Much Does a Website Cost in Dallas? · PlugitAI",
    description: "Honest 2026 pricing for Dallas website design. Launch tier $299.99. Custom Studio from $4,000. No monthly retainer required. What each price actually gets you, side by side.",
    canonical: "https://plugitai.com/website-cost-dallas",
  });
  return (
    <SeoLayout
      eyebrow="Pricing · Dallas · 2026"
      h1="What Does a Dallas Website Cost?"
      intro="Short answer: less than the average agency charges for a discovery call. Long answer: it depends on whether you need a rebuild-and-ship or a full bespoke AI workforce. Here's the actual breakdown."
      testId="page-website-cost-dallas"
    >
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="rounded-lg border border-blue-400/40 p-6 bg-blue-500/[0.04]">
          <div className="text-[10px] uppercase tracking-[0.28em] font-black text-blue-600">Launch tier</div>
          <div className="flex items-baseline gap-2 mt-2">
            <div className="text-4xl font-black">$299.99</div>
            <div className="text-xs text-slate-500">one-time</div>
          </div>
          <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
            <li>· Up to 50 SEO pages</li>
            <li>· AI receptionist chat widget</li>
            <li>· Google Business + schema markup</li>
            <li>· Same-week turnaround</li>
            <li>· 30-day launch support</li>
          </ul>
          <div className="mt-5">
            <CTA to="/website-launch" primary label="Lock it in" testId="cta-cost-launch" />
          </div>
        </div>
        <div className="rounded-lg border border-amber-300 p-6 bg-amber-50">
          <div className="text-[10px] uppercase tracking-[0.28em] font-black text-amber-500">Custom Studio</div>
          <div className="flex items-baseline gap-2 mt-2">
            <div className="text-4xl font-black">from $4,000</div>
            <div className="text-xs text-slate-500">scoped per build</div>
          </div>
          <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
            <li>· Bespoke art direction + motion</li>
            <li>· Full 6-bot AI workforce</li>
            <li>· Stripe checkout / e-com if needed</li>
            <li>· Own your stack (hosted or exported)</li>
            <li>· 90-day launch support</li>
          </ul>
          <div className="mt-5">
            <CTA to="/custom-studio" label="Scope a build" testId="cta-cost-custom" />
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 p-6 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <h2 className="font-black text-xl mb-3">What the &ldquo;big Dallas agency&rdquo; quote usually looks like</h2>
        <div className="text-sm text-slate-600 space-y-2">
          <p>· <span className="text-red-500 font-black">$8,000 – $25,000</span> up-front build fee (Bop Design, ControlF5, UPQODE, PopArt Studio — all published rates).</p>
          <p>· <span className="text-red-500 font-black">$500 – $2,500 / month</span> &ldquo;retainer&rdquo; for hosting + minor edits.</p>
          <p>· <span className="text-red-500 font-black">3 – 6 month timeline.</span> You&#39;re paying while nothing is live.</p>
          <p>· <span className="text-red-500 font-black">No AI.</span> That&#39;s a separate $12k line item, if they offer it at all.</p>
        </div>
        <p className="text-sm text-slate-500 mt-4">PlugitAI ships the same-week for $299.99 with the AI included. Then you can decide if you want to layer more.</p>
      </div>
      <CrossLinks items={[
        { to: "/website-launch", label: "Website Launch details", desc: "What&#39;s in the $299.99 build." },
        { to: "/custom-studio", label: "Custom Studio details", desc: "When you need more." },
        { to: "/small-business-web-design-dallas", label: "Small business path", desc: "The most common ask." },
      ]}/>
    </SeoLayout>
  );
}

// ── 17. Dallas Web Design Case Studies ──────────────────────────────────
export function DallasWebDesignCaseStudies() {
  useSeo({
    title: "Dallas Web Design Case Studies · PlugitAI",
    description: "Real Dallas web design case studies from PlugitAI — Lone Star Striping Pros, Miss Rodriguez Pest Control, Lone Star Lumpers, iHeartFrenchies. What we built, why, and what changed.",
    canonical: "https://plugitai.com/dallas-web-design-case-studies",
  });
  const cases = [
    {
      to: "/case-studies/lone-star-striping",
      name: "Lone Star Striping Pros",
      city: "Fort Worth · Dallas · Arlington",
      stack: "Launch tier + SalesBot + SEO Watch",
      snap: "266+ live pages, after-hours AI closing, from zero online presence to searchable in every DFW city.",
    },
    {
      to: "/case-studies/miss-rodriguez",
      name: "Miss Rodriguez Pest Control",
      city: "DFW · Plano · Frisco · McKinney · Arlington",
      stack: "Launch tier + AI Receptionist",
      snap: "Sub-$2,000 total build cost, ~16 leads/day, ~50% close rate — commercial + residential pest.",
    },
    {
      to: "/case-studies/lone-star-striping",
      name: "Lone Star Lumpers",
      city: "DFW · warehouse labor · 24/7 dispatch",
      stack: "Launch tier + dispatch inbox",
      snap: "60-min response guarantee, 24/7 dispatch, A+ BBB — freight support without an office manager.",
    },
    {
      to: "/case-studies/iheartfrenchies",
      name: "iHeartFrenchies",
      city: "Texas · direct-to-consumer",
      stack: "Custom Studio + Lead-Qual + Follow-Up",
      snap: "Bespoke breeder brand with a lead-qual bot that filters tire-kickers before they hit the phone.",
    },
  ];
  return (
    <SeoLayout
      eyebrow="Case studies · Dallas / Fort Worth"
      h1="Dallas Web Design Case Studies."
      intro="Not testimonials — receipts. Real DFW businesses we shipped for, what stack we used, and what changed after launch. Click any one to read the full story."
      testId="page-dallas-web-design-case-studies"
    >
      <div className="grid md:grid-cols-2 gap-5">
        {cases.map((c) => (
          <Link
            key={c.name}
            to={c.to}
            className="rounded-lg border border-slate-200 p-6 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-blue-400 hover:bg-slate-50 transition-colors"
            data-testid={`case-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} className="text-amber-500" />
              <div className="text-[10px] uppercase tracking-[0.24em] font-black text-slate-400">{c.city}</div>
            </div>
            <div className="text-lg font-black">{c.name}</div>
            <div className="text-[11px] text-blue-600 font-black mt-1">{c.stack}</div>
            <div className="text-sm text-slate-600 mt-3">{c.snap}</div>
            <div className="mt-4 text-[11px] uppercase tracking-[0.24em] font-black text-slate-400 flex items-center gap-1">
              Read the story <ArrowRight size={12} />
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-12 flex flex-wrap gap-3">
        <CTA to="/#contact?intent=case_studies" primary label="Be the next case study" icon={Rocket} testId="cta-cases-be-next" />
        <CTA to="/website-design-dallas" label="Dallas landing page" testId="cta-cases-landing" />
      </div>
      <CrossLinks items={[
        { to: "/contractor-website-design-dallas", label: "Contractors", desc: "Every DFW trade we&#39;ve shipped." },
        { to: "/small-business-web-design-dallas", label: "Small business", desc: "The most common launch path." },
        { to: "/about-kit", label: "About Kit", desc: "Who&#39;s actually building all this." },
      ]}/>
    </SeoLayout>
  );
}

// ── FAQ block — used on the 4 new commercial-intent Dallas service pages.
//     Rendered as visible <details>/<summary> so Google can parse the Q&A
//     text alongside the FAQPage JSON-LD block emitted by useServiceSchema.
function FAQ({ items, testId = "faq" }) {
  return (
    <div className="mt-14" data-testid={testId}>
      <h2 className="font-black text-xl md:text-2xl mb-6">Frequently asked</h2>
      <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
        {items.map((it) => (
          <details key={it.q} className="group py-4">
            <summary className="cursor-pointer flex items-start justify-between gap-4 text-sm md:text-base font-black list-none">
              <span>{it.q}</span>
              <span className="text-blue-600 shrink-0 mt-0.5 group-open:rotate-45 transition-transform">＋</span>
            </summary>
            <div className="mt-3 text-sm text-slate-600 leading-relaxed">{it.a}</div>
          </details>
        ))}
      </div>
    </div>
  );
}

// ── 18. AI Automation Dallas ────────────────────────────────────────────
// Targets: "AI Automation Dallas", "Business Automation Dallas", "Small Business AI Automation"
export function AIAutomationDallas() {
  const url = "https://plugitai.com/ai-automation-dallas";
  useSeo({
    title: "AI Automation Dallas · Business Workflow Automation | PlugitAI",
    description: "AI business automation for Dallas–Fort Worth companies. Lead capture, qualification, follow-up, scheduling, invoicing, and customer service — running while you sleep. Built by PlugitAI in DFW.",
    canonical: url,
  });
  const faq = [
    { q: "What is AI business automation, in plain English?", a: "It's software that does the repetitive back-office work your team does today — answering the same customer questions, following up with cold leads, sending appointment reminders, chasing invoices — but 24/7 and without payroll. PlugitAI wires it directly into your website, phone system, and CRM." },
    { q: "How is this different from Zapier or Make.com?", a: "Zapier connects apps. PlugitAI builds the intelligence that runs on top — an AI agent that actually reads the message, decides what to do, and executes. We use Zapier and Make.com under the hood when they're the right tool, but the decision layer is ours." },
    { q: "What does it cost for a Dallas small business?", a: "A launch-tier automation on top of the $299.99 website is included in the build. Deeper workflow buildouts (e.g., inventory sync, custom triage bots, multi-system integrations) start at $500 per workflow and scope from there. No monthly retainer required." },
    { q: "How long does it take to launch?", a: "Standard workflows (lead capture, appointment booking, review requests, follow-up drips) go live in the same week as the site. Custom workflows involving accounting, inventory, or medical systems typically take 2–4 weeks depending on API access." },
    { q: "Do you serve Dallas only, or all of DFW?", a: "All of DFW plus surrounding — Dallas, Fort Worth, Plano, Frisco, McKinney, Arlington, Irving, Grand Prairie, Denton, Garland, and the 60+ smaller cities in the metroplex. We also serve clients nationwide but Dallas is home base." },
  ];
  useServiceSchema({
    url,
    service: {
      name: "AI Business Automation Dallas",
      description: "AI-powered business workflow automation for Dallas / Fort Worth small businesses — lead capture, follow-up, scheduling, invoicing, and customer service on autopilot.",
      offers: { "@type": "Offer", priceCurrency: "USD", price: "500", availability: "https://schema.org/InStock" },
    },
    faq,
  });
  return (
    <SeoLayout
      eyebrow="Business automation · Dallas · Fort Worth"
      h1="AI Business Automation, Dallas."
      intro="You're running the whole show. PlugitAI hands you an AI teammate that answers the phone, qualifies the lead, books the appointment, reminds the customer, chases the invoice, and pings you the second something needs a human. Built in DFW. Wired into your existing systems — not a bolt-on."
      testId="page-ai-automation-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=ai_automation" primary label="Book an automation audit" icon={Workflow} testId="cta-automation-audit" />
        <CTA to="/ai-workforce" label="See the workforce map" testId="cta-automation-map" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { t: "Lead capture", d: "Website chat, form, or phone → CRM + SMS to you in under 30 seconds." },
          { t: "Lead qualification", d: "Bot triages by budget, timeline, and intent before it hits your inbox." },
          { t: "Appointment booking", d: "Two-way calendar sync. Customer picks a slot in-chat. Confirmations + reminders automatic." },
          { t: "Follow-up drips", d: "Ghosted lead at 3 days · 7 days · 14 days · 30 days. Email + SMS. Killed on reply." },
          { t: "Invoice chase", d: "Late-invoice bot that emails, texts, and escalates without you lifting a finger." },
          { t: "Review capture", d: "Post-job SMS asks for a Google review the moment the job is done." },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">{c.t}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-12 rounded-lg border border-slate-200 p-6 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="text-[10px] uppercase tracking-[0.28em] font-black text-blue-600 mb-2">Real example · Miss Rodriguez Pest Control · DFW</div>
        <p className="text-sm text-slate-600 leading-relaxed">
          Miss Rodriguez used to lose leads to voicemail after 5 p.m. PlugitAI wired her site chat → SMS to her phone → auto-booking calendar. Same-day quotes on 92% of inbound leads. ~16 booked jobs per day at ~50% close rate. Total automation build: under $2,000.
        </p>
        <div className="mt-3">
          <Link to="/case-studies/miss-rodriguez" className="text-[12px] uppercase tracking-[0.24em] font-black text-blue-600 hover:text-blue-300" data-testid="cta-automation-case">
            Read the case study →
          </Link>
        </div>
      </div>
      <FAQ items={faq} testId="faq-ai-automation-dallas" />
      <CrossLinks items={[
        { to: "/ai-chatbot-dallas", label: "AI Chatbot Dallas", desc: "The front-desk piece of automation." },
        { to: "/ai-sales-agent-dallas", label: "AI Sales Agent Dallas", desc: "The bot that actually closes." },
        { to: "/local-seo-dallas", label: "Local SEO Dallas", desc: "So the traffic is there to automate." },
      ]}/>
    </SeoLayout>
  );
}

// ── 19. AI Chatbot Dallas ───────────────────────────────────────────────
// Targets: "AI Chatbot Dallas", "AI Chatbot for Small Business"
export function AIChatbotDallas() {
  const url = "https://plugitai.com/ai-chatbot-dallas";
  useSeo({
    title: "AI Chatbot Dallas · For Small Business Websites | PlugitAI",
    description: "AI chatbots for Dallas small business websites — customer service, FAQ, and lead capture 24/7. Included with every PlugitAI website. From $299.99. Serving DFW.",
    canonical: url,
  });
  const faq = [
    { q: "What can an AI chatbot actually do for my Dallas business?", a: "Three things: answer routine customer questions (hours, pricing, services, location), capture new leads (name, phone, urgency, intent), and hand hot conversations to you. Trained on your business, in your voice, always polite, always on." },
    { q: "How is it different from those generic chatbot widgets?", a: "Generic widgets (Intercom, Drift, Tidio) run on templates and canned rules. PlugitAI's chatbot is trained on your actual services, pricing, and policies. It also plugs into your CRM automatically — the lead is captured before the chat window even closes." },
    { q: "Do I need a separate chatbot subscription?", a: "No. Every PlugitAI website ($299.99 launch tier and up) includes the AI chatbot at no extra monthly cost. Custom Studio builds get the full 6-bot AI workforce." },
    { q: "Will it embarrass my brand?", a: "The bot is guardrailed — no off-topic answers, no invented pricing, no politics. If it doesn't know something, it politely says so and offers to text you the question. You approve the master prompt before launch." },
  ];
  useServiceSchema({
    url,
    service: {
      name: "AI Chatbot Dallas",
      description: "AI chatbot for Dallas small business websites — 24/7 customer service, FAQ handling, and lead capture. Included with every PlugitAI website build.",
      offers: { "@type": "Offer", priceCurrency: "USD", price: "0", availability: "https://schema.org/InStock", description: "Included with $299.99 website launch tier and above." },
    },
    faq,
  });
  return (
    <SeoLayout
      eyebrow="Chatbot · small business · Dallas"
      h1="AI Chatbot, Dallas."
      intro="Every visitor to your Dallas website gets a real answer within seconds — not a callback on Monday. PlugitAI's chatbot is trained on your business, plugs straight into your CRM, and is included with every $299.99 website launch. No separate subscription."
      testId="page-ai-chatbot-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=chatbot" primary label="Turn one on for my site" icon={MessageSquare} testId="cta-chatbot-launch" />
        <CTA to="/ai-receptionist" label="See the AI Receptionist" testId="cta-chatbot-receptionist" />
      </div>
      <div className="grid md:grid-cols-2 gap-8">
        <article>
          <h2 className="font-black text-xl mb-3">What it answers</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Hours, service area, pricing basics, payment methods.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Service descriptions, timelines, and FAQs.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Booking availability + calendar links.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> Order status, appointment reminders, follow-ups.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-blue-600 shrink-0 mt-0.5" /> &ldquo;Talk to a human&rdquo; hand-off — right to your phone.</li>
          </ul>
        </article>
        <article>
          <h2 className="font-black text-xl mb-3">What it does NOT do</h2>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" /> Invent pricing or make promises off-brand.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" /> Weigh in on politics, religion, or off-topic subjects.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" /> Store or share customer PII outside your CRM.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-red-500 shrink-0 mt-0.5" /> Attempt to close a deal without your approval (see AI Sales Agent for that).</li>
          </ul>
        </article>
      </div>
      <FAQ items={faq} testId="faq-ai-chatbot-dallas" />
      <CrossLinks items={[
        { to: "/ai-sales-agent-dallas", label: "AI Sales Agent Dallas", desc: "The closer, not the front desk." },
        { to: "/ai-automation-dallas", label: "AI Automation Dallas", desc: "Automate the workflow behind the chat." },
        { to: "/ai-receptionist", label: "AI Receptionist", desc: "The bot spec sheet." },
      ]}/>
    </SeoLayout>
  );
}

// ── 20. AI Sales Agent Dallas ───────────────────────────────────────────
// Targets: "AI Sales Agent", "AI Sales Bot Dallas", "AI Website Builder" (partial)
export function AISalesAgentDallas() {
  const url = "https://plugitai.com/ai-sales-agent-dallas";
  useSeo({
    title: "AI Sales Agent Dallas · 24/7 Lead Closer for Small Business | PlugitAI",
    description: "AI sales agent that qualifies leads, handles objections, and closes deals 24/7 for Dallas / Fort Worth businesses. Trained on your services, guardrailed, and pinged straight to your phone. From $299.99.",
    canonical: url,
  });
  const faq = [
    { q: "How is an AI sales agent different from a chatbot?", a: "A chatbot answers questions. A sales agent moves people toward a decision. PlugitAI's sales agent explains services, handles the 5 objections you get every day (price, timeline, competitor, trust, urgency), recommends the next step, and pings your phone the moment intent fires." },
    { q: "Will it close the deal without me?", a: "Only if you approve it. You define what the bot can offer (basic quotes, appointment booking, deposit taking) and where it hands off to you. Most Dallas businesses let the bot handle the qualifier and the human closes the deal." },
    { q: "What does it do at 2 a.m.?", a: "The same thing it does at 2 p.m. Answers, qualifies, books, and follows up. In our Lone Star Striping case study the bot closed after-hours estimates that the owner would have missed entirely." },
    { q: "Does it work with my existing website?", a: "Yes — we can drop the sales agent into any existing site (WordPress, Squarespace, Wix, custom). But if your site is slow or unranked, the bot has nothing to work with. We usually recommend the $299.99 launch tier + sales agent as one package." },
  ];
  useServiceSchema({
    url,
    service: {
      name: "AI Sales Agent Dallas",
      description: "24/7 AI sales agent that qualifies leads, handles objections, and closes deals for Dallas / Fort Worth businesses. Trained on your services and guardrailed.",
      offers: { "@type": "Offer", priceCurrency: "USD", price: "299.99", availability: "https://schema.org/InStock" },
    },
    faq,
  });
  return (
    <SeoLayout
      eyebrow="Sales agent · 24/7 · Dallas"
      h1="AI Sales Agent, Dallas."
      intro="A trained AI teammate that explains your services, handles the 5 objections you hear every day, and hands hot buyers straight to your phone — 24/7. Included on every PlugitAI Dallas website. Built for closing, not just chatting."
      testId="page-ai-sales-agent-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=sales_agent" primary label="Deploy on my site" icon={Zap} testId="cta-sales-agent-deploy" />
        <CTA to="/case-studies/lone-star-striping" label="See it close after 5 p.m." testId="cta-sales-agent-case" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { t: "Explains services", d: "Your offering, in your voice, on-brand — validated by you before launch." },
          { t: "Handles objections", d: "Price, timeline, competitor, trust, urgency — approved responses only." },
          { t: "Qualifies buyers", d: "Budget · timeline · decision authority · pain point — captured cleanly." },
          { t: "Books appointments", d: "Two-way calendar sync. Buyer picks a slot in-chat. Confirmation SMS + email." },
          { t: "Pings your phone", d: "Hot lead? SMS to Kit's number (or yours) within 30 seconds of intent firing." },
          { t: "Never sleeps", d: "2 a.m. inquiries get the same response as 2 p.m. Weekends. Holidays. Every day." },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">{c.t}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
          </div>
        ))}
      </div>
      <FAQ items={faq} testId="faq-ai-sales-agent-dallas" />
      <CrossLinks items={[
        { to: "/ai-chatbot-dallas", label: "AI Chatbot Dallas", desc: "The front-desk (not the closer)." },
        { to: "/ai-automation-dallas", label: "AI Automation Dallas", desc: "Everything the sales agent triggers." },
        { to: "/website-design-dallas", label: "Dallas Website Design", desc: "The site the sales agent lives on." },
      ]}/>
    </SeoLayout>
  );
}

// ── 21. Web Development Dallas ──────────────────────────────────────────
// Targets: "Web Development Dallas" (distinct from design — this is the code-heavy sibling)
export function WebDevelopmentDallas() {
  const url = "https://plugitai.com/web-development-dallas";
  useSeo({
    title: "Web Development Dallas · Custom Builds + API Integrations | PlugitAI",
    description: "Custom web development in Dallas — bespoke code, API integrations, payment systems, customer portals, and complex builds beyond what a template can do. Serving DFW small business.",
    canonical: url,
  });
  const faq = [
    { q: "What's the difference between web design and web development?", a: "Design is what the site looks like. Development is what it does. If your project needs custom code (a booking system for a specific workflow, an inventory sync, a customer portal, a payment engine, a data import), that's development. PlugitAI does both, but this page is for projects that need the code work." },
    { q: "What kinds of Dallas businesses hire PlugitAI for development?", a: "Businesses that outgrew their WordPress / Wix / Squarespace site — an e-commerce shop with 200+ SKUs and inventory sync, a service business with a custom booking flow, a professional office with a client portal, a compounding pharmacy with state-restriction logic (see LeenRx / BestRx case studies)." },
    { q: "What tech stack do you use?", a: "React + FastAPI + MongoDB for full-stack projects. Plain HTML + Tailwind for pure marketing sites. We integrate anything with an API: Stripe, Twilio, SendGrid, Google Calendar, HubSpot, Salesforce, QuickBooks, Zapier, Make.com, etc." },
    { q: "How is this priced?", a: "Custom development is scoped per project starting at $4,000. The $299.99 launch tier is for template-driven sites; anything requiring custom code goes through Custom Studio. Book a 15-minute scoping call and we'll quote it exactly." },
  ];
  useServiceSchema({
    url,
    service: {
      name: "Web Development Dallas",
      description: "Custom web development in Dallas / Fort Worth — bespoke code, API integrations, payment systems, customer portals, and complex builds beyond template.",
      offers: { "@type": "Offer", priceCurrency: "USD", price: "4000", availability: "https://schema.org/InStock" },
    },
    faq,
  });
  return (
    <SeoLayout
      eyebrow="Development · custom code · Dallas"
      h1="Web Development, Dallas."
      intro="When your project needs actual code — API integrations, custom booking systems, payment engines, inventory sync, customer portals — PlugitAI is the DFW shop that ships it. React + FastAPI + MongoDB. Built by Kit Garcia in Dallas."
      testId="page-web-development-dallas"
    >
      <div className="flex flex-wrap gap-3 mb-10">
        <CTA to="/#contact?intent=web_development" primary label="Scope a build" icon={Code2} testId="cta-webdev-scope" />
        <CTA to="/custom-studio" label="See Custom Studio" testId="cta-webdev-custom" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { t: "API integrations", d: "Stripe, Twilio, SendGrid, Google Calendar, QuickBooks, HubSpot, Salesforce." },
          { t: "Payment systems", d: "Stripe checkout, subscriptions, invoicing, multi-currency, deposit capture." },
          { t: "Customer portals", d: "Login-gated dashboards where clients see orders, invoices, and messages." },
          { t: "E-commerce logic", d: "Inventory sync, tax rules, shipping calculators, abandoned-cart drips." },
          { t: "Booking systems", d: "Multi-provider, multi-service calendars with real-time availability." },
          { t: "Data import + sync", d: "One-time migrations or ongoing syncs between your systems." },
        ].map((c) => (
          <div key={c.t} className="rounded-lg border border-slate-200 p-5 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="text-sm font-black">{c.t}</div>
            <div className="text-[12px] text-slate-500 mt-1">{c.d}</div>
          </div>
        ))}
      </div>
      <div className="mt-12 rounded-lg border border-slate-200 p-6 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="text-[10px] uppercase tracking-[0.28em] font-black text-blue-600 mb-2">Real dev projects · shipped by PlugitAI</div>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-600">
          <div>
            <div className="font-black text-slate-900">LeenRx + BestRx · Telehealth</div>
            <div className="mt-1 text-[12px] text-slate-500">Nationwide GLP-1 direct-to-patient. Symptom triage bot, Stripe checkout, per-state restriction logic, pharmacy dispatch integration.</div>
          </div>
          <div>
            <div className="font-black text-slate-900">CargoPros · Freight quoting</div>
            <div className="mt-1 text-[12px] text-slate-500">Instant-quote form → SMS lead capture → booking calendar → dispatch inbox. Custom pricing engine.</div>
          </div>
        </div>
      </div>
      <FAQ items={faq} testId="faq-web-development-dallas" />
      <CrossLinks items={[
        { to: "/custom-studio", label: "Custom Studio", desc: "Where these builds live." },
        { to: "/ai-automation-dallas", label: "AI Automation Dallas", desc: "The intelligence layer on top." },
        { to: "/website-cost-dallas", label: "Dallas website cost", desc: "How development is scoped and priced." },
      ]}/>
    </SeoLayout>
  );
}

